<template>
  <div class="multi-step-form" v-if="ready">
    <!-- top bar -->
    <div class="d-flex justify-content-between align-items-center mt-2 px-3">
      <button class="btn btn-sm border-0 rounded bg-transparent d-flex align-items-center fw-bold text-white" @click="$router.back()">
        <i class='bx bx-menu-alt-left fs-3 icon-white'></i>
      </button>
      <button class="btn btn-sm border-0 rounded bg-transparent d-flex align-items-center fw-bold text-white">
        <i class="fa-solid fa-circle-arrow-right fs-5 me-2 icon-white"></i>
        <a href="/" class="text-xxs text-white text-decoration-none">Logout</a>
      </button>
    </div>

    <!-- single card body (uses same card look as onboarding) -->
    <div class="forms-container mt-4"
        v-if="!is_loading && !redirecting && attributes_list.length"
    >
      <div class="carousel-container">
        <div class="form-wrapper active">
          <div class="form-card mt-0 shadow-lg">
            <div class="form-header fw-bold text-x mt-3 shadow-lg">
              Fields Required
            </div>

            <div class="section-container active">
              <div class="form-fields-container p-3">
                <div v-if="is_loading" class="text-xxs text-muted">Loading attributes…</div>

                <template v-else>
                  <div v-if="!attributes_list.length" class="text-xxs text-muted">
                    No Fields Required found for this profile.
                  </div>

                  <div
                    v-for="attribute_item in attributes_list"
                    :key="'eta-' + (attribute_item.entity_type_attribute_id ?? attribute_item.id)"
                    class="col-12 col-md-12 mb-3"
                  >
                    <template v-if="!is_skipped_type(attribute_item)">
                      <label class="fw-medium text-xs d-block mb-1">
                        {{ get_attr_label(attribute_item) }}
                      </label>

                      <!-- short_text -->
                      <input
                        v-if="canonical_type(attribute_item) === 'short_text'"
                        type="text"
                        class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                        v-model="attributes_form[get_attr_id(attribute_item)]"
                        :placeholder="get_attr_label(attribute_item)"
                      />

                      <!-- long_text -->
                      <textarea
                        v-else-if="canonical_type(attribute_item) === 'long_text'"
                        rows="2"
                        class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                        v-model="attributes_form[get_attr_id(attribute_item)]"
                        :placeholder="get_attr_label(attribute_item)"
                      ></textarea>

                      <!-- number -->
                      <input
                        v-else-if="canonical_type(attribute_item) === 'number'"
                        type="number"
                        class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                        v-model="attributes_form[get_attr_id(attribute_item)]"
                        :placeholder="get_attr_label(attribute_item)"
                      />

                      <!-- date -->
                      <input
                        v-else-if="canonical_type(attribute_item) === 'date'"
                        type="date"
                        class="form-control form-control-sm text-xxs bg-light border-1 py-1 rounded-lg"
                        v-model="attributes_form[get_attr_id(attribute_item)]"
                      />

                      <!-- single_select (radio) -->
                      <div v-else-if="canonical_type(attribute_item) === 'single_select'" class="radio-group">
                        <div
                          v-for="(opt, i) in get_options(attribute_item)"
                          :key="'opt-' + get_attr_id(attribute_item) + '-' + i"
                          class="form-check"
                        >
                          <input
                            class="form-check-input"
                            type="radio"
                            :name="'attr-' + get_attr_id(attribute_item)"
                            :id="'attr-' + get_attr_id(attribute_item) + '-' + i"
                            :value="opt.value"
                            v-model="attributes_form[get_attr_id(attribute_item)]"
                          />
                          <label
                            class="form-check-label text-xxs"
                            :for="'attr-' + get_attr_id(attribute_item) + '-' + i"
                          >
                            {{ opt.text }}
                          </label>
                        </div>
                      </div>

                      <!-- multi_select (checkboxes) -->
                      <div v-else-if="canonical_type(attribute_item) === 'multi_select'">
                        <div
                          v-for="(opt, i) in get_options(attribute_item)"
                          :key="'mopt-' + get_attr_id(attribute_item) + '-' + i"
                          class="form-check"
                        >
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'mopt-' + get_attr_id(attribute_item) + '-' + i"
                            :value="opt.value"
                            v-model="attributes_form[get_attr_id(attribute_item)]"
                          />
                          <label
                            class="form-check-label text-xxs"
                            :for="'mopt-' + get_attr_id(attribute_item) + '-' + i"
                          >
                            {{ opt.text }}
                          </label>
                        </div>
                      </div>

                      <!-- Fallback for unknown types -->
                      <div v-else class="text-xxs text-muted">
                        Unsupported input type: {{ canonical_type(attribute_item) }}
                      </div>
                    </template>

                    <!-- Show skipped types info -->
                    <div v-else class="text-xxs text-muted">
                      <i>{{ get_attr_label(attribute_item) }}</i> - This field type ({{ canonical_type(attribute_item) }}) is handled separately.
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2 mt-4">
                    <button
                      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold mx-auto"
                      @click="submit_attribute_values"
                      :disabled="is_submitting || !attributes_list.length"
                    >
                      {{ is_submitting ? 'Saving…' : 'Save Attributes' }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  get_headers,
  LIST_ATTRIBUTES_AND_VALUES_URL,
  SUBMIT_ATTRIBUTE_VALUES_URL,
  GET_APPLICATION_FORMS,
  GET_FORM_PROGRESS,
  get_token,
} from '@/utils/constants/constants';

export default {
  name: 'AttributeValuesForm',
  mixins: [alert_mixin],

  async beforeRouteEnter(to, from, next) {
    const headers = get_headers();
    const token = get_token();

    const pick_int = (value) => {
      const num = Number(value);
      return Number.isInteger(num) && num > 0 ? num : null;
    };

    const try_ids = [
      to.params?.entity_profile_id,
      to.params?.profile_id,
      to.query?.entity_profile_id,
      to.query?.profile_id,
      sessionStorage.getItem('entity_profile_id'),
    ];
    let entity_profile_id = null;
    for (const candidate of try_ids) {
      const parsed = pick_int(candidate);
      if (parsed) { entity_profile_id = parsed; break; }
    }

    async function fetch_entity_profile_from_api() {
      try {
        const resp = await axios.get(GET_APPLICATION_FORMS, { headers });
        const data = resp?.data;
        const form = Array.isArray(data?.data) ? data.data[0] : data?.data;
        return pick_int(form?.entity_profile);
      } catch { return null; }
    }

    if (!entity_profile_id) {
      entity_profile_id = await fetch_entity_profile_from_api();
    }
    if (!entity_profile_id) {
      return next(vm => vm.$router.replace({ path: '/entity_onboarding_forms', query: { section_index: 0 } }));
    }

    try {
      const resp = await axios.get(LIST_ATTRIBUTES_AND_VALUES_URL, {
        headers,
        params: { entity_profile_id },
      });
      const success = resp?.data?.status === 'success';
      const list = success && Array.isArray(resp.data.data) ? resp.data.data : [];

      if (!list.length) {
        return next(vm => vm.$router.replace({ path: '/entity_onboarding_forms', query: { section_index: 0 } }));
      }

      next((component) => {
        component.headers = headers;
        component.token = token;
        component.entity_profile_id = entity_profile_id;
        component.user_id = sessionStorage.getItem('user_id') || null;
        component.application_form_id = Number(sessionStorage.getItem('application_form_id')) || null;

        component.attributes_list = list;
        component.reset_to_server_values();

        component.is_loading = false;
        component.ready = true;
      });
    } catch {
      next(vm => vm.$router.replace({ path: '/entity_onboarding_forms', query: { section_index: 0 } }));
    }
  },

  async beforeRouteUpdate(to, from, next) {
    this.ready = false;
    this.is_loading = true;
    this.redirecting = false;
    // Reuse the same logic:
    this.$options.beforeRouteEnter.call(this, to, from, next);
  },

  data: () => ({
    headers: '',
    token: '',
    user_id: null,
    application_form_id: null,

    entity_profile_id: null,

    is_loading: true,
    is_submitting: false,

    attributes_list: [],
    attributes_form: {},
    ready: false,
    redirecting: false,
  }),

  async mounted() {
    this.headers = get_headers();
    this.token = get_token();
    this.user_id = sessionStorage.getItem('user_id') || null;
    this.application_form_id = Number(sessionStorage.getItem('application_form_id')) || null;

    await this.resolve_entity_profile_id();
    if (!this.entity_profile_id) {
      this.show_error('Could not resolve entity profile.');
      this.is_loading = false;
      return;
    }
    await this.get_attributes_and_values();
  },
  methods: {
    async resolve_entity_profile_id() {
      const id_from_route = this.get_entity_profile_id_from_route();
      if (id_from_route !== null) {
        this.entity_profile_id = id_from_route;
        return;
      }

      const id_from_api = await this.get_entity_profile_id_from_api();
      if (id_from_api !== null) {
        this.entity_profile_id = id_from_api;
      }
    },

    get_entity_profile_id_from_route() {
      return this.first_valid_int([
        this.$route?.params?.entity_profile_id,
        this.$route?.params?.profile_id,
        this.$route?.query?.entity_profile_id,
        this.$route?.query?.profile_id,
      ]);
    },

    async get_entity_profile_id_from_api() {
      // no try/catch block here; use promise chaining to avoid extra branches
      return axios
        .get(GET_APPLICATION_FORMS, { headers: this.headers })
        .then(res => {
          const api_data = res?.data;
          const form_data = Array.isArray(api_data?.data) ? api_data.data[0] : api_data?.data;
          return this.parse_positive_int(form_data?.entity_profile);
        })
        .catch(() => null);
    },

    first_valid_int(candidates) {
      for (const candidate of candidates) {
        const parsed = this.parse_positive_int(candidate);
        if (parsed !== null) return parsed;
      }
      return null;
    },

    parse_positive_int(value) {
      const n = Number(value);
      return Number.isInteger(n) && n > 0 ? n : null;
    },



    async check_form_progress() {
      const request_params = this._build_progress_params();
      if (!request_params) {
        return { form_completed: false, current_section_index: 0 };
      }

      const api_response_data = await this._fetch_progress_safe(request_params);
      const normalized = this._normalize_progress(api_response_data);

      if (normalized.application_form_id) {
        this.application_form_id = normalized.application_form_id;
        sessionStorage.setItem('application_form_id', String(this.application_form_id));
      }

      return {
        form_completed: normalized.form_completed,
        current_section_index: normalized.current_section_index
      };
    },

    _build_progress_params() {
      if (this.application_form_id) {
        return { application_form_id: this.application_form_id };
      }
      if (this.user_id) {
        return { user_id: this.user_id };
      }
      return null;
    },

    async _fetch_progress_safe(request_params) {
      try {
        const response = await axios.get(GET_FORM_PROGRESS, {
          headers: { 'Content-Type': 'application/json', Authorization: `Token ${this.token}` },
          params: request_params
        });
        return response?.data || null;
      } catch {
        return null;
      }
    },

    _normalize_progress(api_data) {
      if (!api_data || api_data.status !== 'success') {
        return {
          application_form_id: null,
          form_completed: false,
          current_section_index: 0
        };
      }
      return {
        application_form_id: api_data.application_form_id || null,
        form_completed: Boolean(api_data.is_completed),
        current_section_index: Number.isInteger(api_data.current_section_index)
          ? api_data.current_section_index
          : 0
      };
    },


    get_attr_id(attribute_item) {
      return attribute_item?.entity_type_attribute_id ?? attribute_item?.id;
    },

    get_attr_label(attribute_item) {
      return attribute_item?.question?.question_text || attribute_item?.name || 'Attribute';
    },

    get_question_type(attribute_item) {
      return String(attribute_item?.question?.question_type || '').trim().toLowerCase();
    },

    canonical_type(attribute_item) {
      const raw_type = this.get_question_type(attribute_item);
      const alias_map = {
        short_text: ['short_text', 'text'],
        long_text: ['long_text', 'textarea'],
        number: ['number', 'numeric'],
        date: ['date', 'datetime', 'date_time'],
        single_select: ['single_select', 'radio', 'single select'],
        multi_select: ['multi_select', 'checkboxes', 'multi select'],
        file: ['file'],
        facial_verification: ['facial_verification']
      };

      for (const canonical_key in alias_map) {
        if (alias_map[canonical_key].includes(raw_type)) return canonical_key;
      }
      return raw_type; // Return original if no alias found
    },

    is_skipped_type(attribute_item) {
      const canonical = this.canonical_type(attribute_item);
      return canonical === 'file' || canonical === 'facial_verification';
    },

    // --- choices helpers ---
    get_options(attribute_item) {
      const raw_choices = Array.isArray(attribute_item?.question?.choices)
        ? attribute_item.question.choices
        : [];
      // Always normalize to { value, text }
      return raw_choices.map(choice => ({ value: choice.id, text: choice.choice }));
    },

    get_label_from_choice_id(attribute_item, choice_id) {
      const options = this.get_options(attribute_item);
      const found_option = options.find(o => String(o.value) === String(choice_id));
      return found_option ? found_option.text : choice_id; // fallback to raw if not found
    },

    to_array_values(raw_value) {
      if (Array.isArray(raw_value)) return raw_value;
      if (raw_value == null || raw_value === '') return [];
      const trimmed_value = String(raw_value).trim();
      // Try JSON array format first
      if (trimmed_value.startsWith('[') && trimmed_value.endsWith(']')) {
        try {
          const parsed_json = JSON.parse(trimmed_value);
          return Array.isArray(parsed_json) ? parsed_json : [parsed_json];
        } catch (_) {
          // fall through to CSV
        }
      }
      // Fallback: CSV string
      return trimmed_value.split(',').map(segment => segment.trim()).filter(Boolean);
    },

    // --- convert ids -> labels before submit (select questions) ---
    normalize_value_for_submit(attribute_item, raw_value) {
      const canonical = this.canonical_type(attribute_item);

      if (canonical === 'single_select') {
        // radio/single select: v-model holds the id; submit the label
        return this.get_label_from_choice_id(attribute_item, raw_value) ?? '';
      }

      if (canonical === 'multi_select') {
        // checkboxes: v-model holds array of ids; submit array of labels (or join if your API prefers)
        const id_array = this.to_array_values(raw_value);
        const label_array = id_array.map(id => this.get_label_from_choice_id(attribute_item, id));
        return label_array; // or return label_array.join(', ') if your backend wants a string
      }

      // other types: submit as-is
      return raw_value ?? '';
    },

    reset_to_server_values() {
      const seeded_model = {};

      this.attributes_list.forEach(attribute_item => {
        const attribute_id = this.get_attr_id(attribute_item);
        const canonical = this.canonical_type(attribute_item);
        const server_value = attribute_item?.answer_value ?? '';

        if (canonical === 'multi_select') {
          // Expect array of ids; normalize anything weird
          if (Array.isArray(server_value)) {
            seeded_model[attribute_id] = server_value
              .map(value_item => {
                if (typeof value_item === 'number') return value_item;
                if (typeof value_item === 'string' && value_item.trim() !== '' && !isNaN(value_item)) {
                  return Number(value_item);
                }
                const match = String(value_item).match(/\d+/);
                return match ? Number(match[0]) : value_item;
              })
              .filter(value_item => value_item !== null && value_item !== undefined);
          } else if (typeof server_value === 'string' && server_value.trim()) {
            // Try JSON first, then CSV
            try {
              const parsed_json = JSON.parse(server_value);
              if (Array.isArray(parsed_json)) {
                seeded_model[attribute_id] = parsed_json.map(v => (typeof v === 'number' ? v : Number(v)));
              } else {
                throw new Error('Not an array');
              }
            } catch (_) {
              seeded_model[attribute_id] = server_value
                .split(',')
                .map(part => {
                  const maybe_num = Number(part.trim());
                  return isNaN(maybe_num) ? part.trim() : maybe_num;
                })
                .filter(v => v !== '');
            }
          } else {
            seeded_model[attribute_id] = [];
          }
        } else if (canonical === 'single_select') {
          // Expect a single id; keep number when possible
          if (server_value !== null && server_value !== undefined && server_value !== '') {
            if (typeof server_value === 'number') {
              seeded_model[attribute_id] = server_value;
            } else if (typeof server_value === 'string') {
              const maybe_num = Number(server_value);
              seeded_model[attribute_id] = isNaN(maybe_num) ? server_value : maybe_num;
            } else {
              seeded_model[attribute_id] = server_value;
            }
          } else {
            seeded_model[attribute_id] = '';
          }
        } else {
          // All other types bind directly
          seeded_model[attribute_id] = server_value ?? '';
        }
      });

      this.attributes_form = seeded_model;
    },

    async get_attributes_and_values() {
      this.is_loading = true;
      try {
        const api_response = await axios.get(LIST_ATTRIBUTES_AND_VALUES_URL, {
          headers: this.headers,
          params: { entity_profile_id: this.entity_profile_id },
        });

        if (api_response?.data?.status === 'success') {
          const attributes = Array.isArray(api_response.data.data) ? api_response.data.data : [];
          this.attributes_list = attributes;

          // NEW: If no attributes, skip this page and redirect
          if (!this.attributes_list.length) {
            // optional toast
            this.redirecting = true;

            // use replace so back button doesn’t return here
            this.$nextTick(() => {
              this.$router.replace({ path: '/entity_onboarding_forms', query: { section_index: 0 } });
            });
            return; // stop further processing/rendering
          }

          this.reset_to_server_values();
        } else {
          this.show_error(api_response?.data?.message || 'Error attributes values retrieval');
        }
      } catch (error) {
        this.show_error(error?.response?.data?.message || error.message || 'Error attributes values retrieval');
      } finally {
        this.is_loading = false;
      }
    },


    async submit_attribute_values() {
      if (!Number.isInteger(this.entity_profile_id) || this.entity_profile_id <= 0) {
        this.show_error('Invalid entity_profile_id');
        return;
      }

      const attribute_values_payload = this.attributes_list.map(attribute_item => {
        const attribute_id = this.get_attr_id(attribute_item);
        const raw_value = this.attributes_form[attribute_id];
        return {
          entity_type_attribute_id: Number(attribute_id),
          values: this.normalize_value_for_submit(attribute_item, raw_value) ?? '',
        };
      });

      this.is_submitting = true;
      try {
        const api_response = await axios.post(
          SUBMIT_ATTRIBUTE_VALUES_URL,
          { entity_profile_id: this.entity_profile_id, attribute_values: attribute_values_payload },
          { headers: this.headers }
        );

        if (api_response?.data?.status === 'success') {
          this.show_success('Attributes saved.');
          const progress_info = await this.check_form_progress();
          const section_index = progress_info?.current_section_index || 0;
          this.$router.push({ path: '/entity_onboarding_forms', query: { section_index } });
        } else {
          this.show_error(api_response?.data?.message || 'Could not save attributes');
        }
      } catch (error) {
        this.show_error(error?.response?.data?.message || error.message || 'Save failed');
      } finally {
        this.is_submitting = false;
      }
    },
  },
};
</script>



<style scoped>
/* Center the form when there's only one section */
.forms-container:has(.form-wrapper:only-child) {
  justify-content: center;
}
.forms-container:has(.form-wrapper:only-child) .form-wrapper {
  flex: 0 0 auto;
  filter: blur(0);
  transform: scale(1);
  opacity: 1;
  z-index: 5;
}

/* Carousel container (default for desktop/tablet) */
.carousel-container {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 calc(50% - min(46.875rem, 80vw) / 2);
  gap: 1.25rem;
  padding: 1.25rem;
  width: 100%;
  margin: 0 auto;
  max-width: 75rem;
  scroll-behavior: smooth;
}

/* Forms container */
.forms-container {
  padding: 0.1875rem;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  box-sizing: border-box;
}

.form-wrapper {
  flex: 0 0 auto;
  width: min(46.875rem, 90vw);
  scroll-snap-align: center;
  transition: all 0.3s ease;
  filter: blur(0.1875rem);
  transform: scale(0.9);
  opacity: 0.7;
  margin: 0 auto;
}
.form-wrapper.active {
  filter: blur(0);
  transform: scale(1);
  opacity: 1;
  z-index: 5;
}

/* Hide scrollbar but keep functionality */
.carousel-container::-webkit-scrollbar { display: none; }
.carousel-container { -ms-overflow-style: none; scrollbar-width: none; }

/* Main multi-step form container */
.multi-step-form {
  position: relative;
  min-height: 100vh;
  height: auto;
  overflow: hidden;
  background-image: var(--client-bg);
  background-blend-mode: multiply;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding-bottom: 1.875rem;
}

/* Utilities */
.btn-back { z-index: 9999; }
.icon-white { color: white !important; }
.section-title { text-transform: capitalize; }

/* Status badges */
.status-badge {
  display: inline-block;
  font-weight: 300;
  border-radius: 0.75rem;
  text-transform: capitalize;
  padding: 0.25rem 0.625rem;
}
.status-badge.verified {
  background-color: #c6f6d5;
  color: #2f855a;
  border: 0.0625rem solid #2f855a;
}
.status-badge.pending {
  color: #FF8C00;
  background-color: #FFF3CD;
  border: 0.0625rem solid #FF8C00;
}

/* Form cards */
.section-container {
  border-radius: 0.5rem;
  z-index: 2;
  opacity: 1;
}
.form-card {
  border-radius: 0.9375rem;
  background: rgba(255,255,255,0.95);
  padding: 1.25rem;
  max-height: 80vh;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  margin: 0 auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}
.form-card::-webkit-scrollbar { width: 0.375rem; }
.form-card::-webkit-scrollbar-track { background: transparent; }
.form-card::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 0.625rem;
}

/* Form header */
.form-header {
  border: 0.125rem solid;
  border-color: var(--secondary-color) !important;
  color: white;
  text-align: center;
  padding: 0.75rem;
  border-radius: 5rem;
  margin-bottom: 1.25rem;
}

/* Progress container (unused visually here but kept for parity) */
.progress-container { display: flex; justify-content: center; align-items: center; margin: 1.25rem 0; }
.step-progress { gap: 0.625rem; flex-wrap: wrap; justify-content: center; padding: 0 0.625rem; }
.step-circle { width: 2.5rem; height: 2.5rem; border-radius: 50%; border: 1px solid var(--secondary-color); background-color: #adb5bd; color: white; font-size: 1rem; font-weight: bold; display: flex; justify-content: center; align-items: center; flex-shrink: 0; }
.step-circle.completed { background-color: var(--primary-color); }
.step-circle.active { background-color: var(--secondary-color); }
.step-circle.upcoming { background-color: #dee2e6; }
.step-line { height: 0.125rem; width: 2.5rem; background-color: black; flex-shrink: 0; }
.step-line.completed { background-color: black; }

/* Tablet */
@media (max-width: 74.9375rem) and (min-width: 48rem) {
  .carousel-container { scroll-padding: 0 calc(50% - 45vw); gap: 0.9375rem; padding: 0.9375rem; margin: 0 5rem; }
  .forms-container { margin: 0 5rem; padding: 4.375rem 0.625rem 1.25rem; }
  .form-wrapper { width: min(37.5rem, 85vw); }
  .form-card { max-height: 75vh; }
  .form-header { font-size: 1.1rem; padding: 0.625rem; }
  .step-circle { width: 2.1875rem; height: 2.1875rem; font-size: 0.875rem; }
  .step-line { width: 1.875rem; }
  .status-badge { font-size: 0.7rem; padding: 0.1875rem 0.5rem; }
}

/* Mobile */
@media (max-width: 47.9375rem) {
  .forms-container { display: flex; flex-direction: row; align-items: flex-start; }
  .carousel-container { flex: 1; display: block; overflow-y: auto; overflow-x: hidden; scroll-snap-type: y mandatory; max-height: 80vh; padding: 0.5rem; }
  .form-wrapper { width: 100% !important; max-width: 100% !important; margin-bottom: 1rem; scroll-snap-align: start; filter: none !important; transform: none !important; opacity: 1 !important; }
  .form-card { max-height: unset; overflow-y: visible; }

  .step-progress { display: none !important; }
  .carousel-container { display: block; overflow-y: auto; overflow-x: hidden; scroll-snap-type: y mandatory; max-height: 80vh; padding: 1rem; }
  .form-wrapper { width: 100% !important; max-width: 100% !important; margin-bottom: 1rem; scroll-snap-align: start; }
}

/* Very small mobile */
@media (max-width: 30rem) {
  .carousel-container { gap: 0.5rem; padding: 0.5rem; margin: -54px 0 1.25rem; }
  .forms-container { margin: 0 1.25rem; padding: 3.125rem 0.3125rem 0.625rem; }
  .form-wrapper { width: min(25rem, 95vw); }
  .form-card { padding: 0.75rem; max-height: 76vh; }
  .form-header { font-size: 0.7rem; padding: 0.5rem; }
  .text-xs, .text-xxs { font-size: 0.6rem !important; }
}
</style>
